import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Box, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { CopyToClipboard as CopyToClipboardComponent } from 'react-copy-to-clipboard';
import { CaseHistoryContext, CaseHistoryData } from '../caseHistory/CaseHistoryContext';

interface CaseHistoryProps {
    searchId: string;
    searchIdType: string;
    clientId: string;
}

interface CopyableFieldProps {
    label: string;
    value: string | null | undefined;
}

const CopyableField: FC<CopyableFieldProps> = ({ label, value }) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = (): void => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const displayValue = value || '--';

    return (
        <Typography
            variant={'subtitle2'}
            color={'textSecondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <strong style={{ color: 'black' }}>{label}:</strong>{' '}
            <CopyToClipboardComponent text={displayValue} onCopy={handleCopy}>
                <Box
                    component={'span'}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}>
                    <Tooltip title={value && (isCopied ? `Copied` : `Copy to Clipboard`)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <b>{displayValue?.length > 30 ? `${displayValue?.substring(0, 30)}...` : displayValue}</b>
                            {value && <ContentCopyOutlinedIcon sx={{ width: '14px', color: 'action.active' }} />}
                        </Box>
                    </Tooltip>
                </Box>
            </CopyToClipboardComponent>
        </Typography>
    );
};

const formatDate = (date: string | null | undefined): string => {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy');
};

const getTimelineIcon = (status: string): React.ReactNode => {
    switch (status?.toLowerCase()) {
        case 'completed':
            return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />;
        case 'failed':
            return <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />;
        default:
            return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />;
    }
};

export const CaseHistory: React.FC<CaseHistoryProps> = ({ clientId, searchId, searchIdType }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [caseHistory, setCaseHistory] = useState<CaseHistoryData | null>(null);
    const { getCaseHistory } = useContext(CaseHistoryContext);
    const [isCopied, setIsCopied] = useState(false);

    const fetchCaseHistory = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);

            // Add null checks for required parameters
            if (!clientId || !searchId || !searchIdType) {
                setError('Missing required parameters');
                return;
            }

            const response = await getCaseHistory(clientId, searchId, searchIdType);
            setCaseHistory(response as CaseHistoryData);
        } catch (err) {
            setError('Failed to fetch case history data');
            console.error('Error fetching case history:', err);
        } finally {
            setLoading(false);
        }
    }, [clientId, searchId, searchIdType, getCaseHistory]);

    useEffect(() => {
        if (searchId && searchIdType && clientId) {
            fetchCaseHistory();
        }
    }, [searchId, searchIdType, clientId, fetchCaseHistory]);

    const handleCopy = (): void => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    if (loading) {
        return (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} minHeight={'200px'}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} minHeight={'200px'}>
                <Typography color={'error'}>{error}</Typography>
            </Box>
        );
    }

    if (!caseHistory?.variables || !caseHistory.variables?.triggerDate) {
        return (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} minHeight={'200px'}>
                <Typography>No case history found</Typography>
            </Box>
        );
    }

    const timelineEvents = [
        { date: caseHistory.variables?.triggerDate, label: 'Case Triggered', status: 'triggered' },
        { date: caseHistory.variables?.searchDate, label: 'Search Initiated', status: 'info' },
        { date: caseHistory.variables?.downloadDate, label: 'Download Completed', status: 'info' },
        {
            date: caseHistory.variables?.firstBatchUploadDate,
            label: 'First Batch Upload',
            status: caseHistory.variables?.status,
        },
        { date: caseHistory.variables?.res1Date, label: 'Res1 Date', status: 'success' },
        { date: caseHistory.variables?.ckycIdGenerationDate, label: 'CKYC ID Generated', status: 'success' },
        { date: caseHistory.variables?.notifiedToCkycDate, label: 'Notified to CKYC', status: 'completed' },
    ].filter((event) => event.date);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Grid container spacing={2} sx={{ maxHeight: '200px', overflow: 'auto' }}>
                        <Grid item xs={6}>
                            <CopyableField label={'Customer Name'} value={caseHistory.variables?.customerName} />
                            <CopyableField label={'CKYC Number'} value={caseHistory.variables?.ckycNumber} />
                            <CopyableField
                                label={'CKYC Reference'}
                                value={caseHistory.variables?.ckycReferenceNumber}
                            />
                            <CopyableField label={'CKYC Operation ID'} value={caseHistory.variables?.ckycOperationId} />
                            <CopyableField label={'LOS App ID'} value={caseHistory.variables?.losAppId} />
                        </Grid>
                        <Grid item xs={6}>
                            <CopyableField label={'Search Parameter'} value={caseHistory.variables?.searchParameter} />
                            <CopyableField label={'CKYC Status'} value={caseHistory.variables?.ckycStatus} />
                            <CopyableField label={'Status'} value={caseHistory.variables?.status} />
                            <CopyableField label={'UCIC'} value={caseHistory.variables?.ucic} />
                        </Grid>
                        {caseHistory.variables?.failureReason && (
                            <Grid item xs={12}>
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Typography
                                        variant={'subtitle2'}
                                        color={'error'}
                                        sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <strong style={{ whiteSpace: 'nowrap' }}>Failure Reason:</strong>{' '}
                                        <CopyToClipboardComponent
                                            text={caseHistory.variables.failureReason}
                                            onCopy={handleCopy}>
                                            <Box
                                                component={'span'}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}>
                                                <Tooltip title={isCopied ? 'Copied' : 'Copy to clipboard'}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}>
                                                        <Typography
                                                            component={'span'}
                                                            sx={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 1,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}>
                                                            {caseHistory.variables.failureReason}
                                                        </Typography>
                                                        <ContentCopyOutlinedIcon
                                                            sx={{ width: '14px', color: 'action.active' }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </CopyToClipboardComponent>
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>

            {/* Timeline Section - Scrollable */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2 }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', px: 10 }}>
                    <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>
                        Status
                    </Typography>
                    <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>
                        Date
                    </Typography>
                </Box>

                <Timeline
                    sx={{
                        '& .MuiTimelineItem-root': {
                            minHeight: 50,
                            '&:before': {
                                flex: 0,
                                padding: 0,
                            },
                        },
                        '& .MuiTimelineContent-root': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 4,
                            padding: '6px 16px',
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                            marginTop: 0,
                            paddingTop: 0,
                            height: '50px',
                        },
                        '& .MuiTimelineDot-root': {
                            margin: 0,
                            padding: 0,
                            borderWidth: 0,
                            backgroundColor: 'transparent',
                        },
                        '& .MuiTimelineConnector-root': {
                            width: '1px',
                            borderLeft: '2px dashed',
                            borderColor: 'divider',
                            backgroundColor: 'transparent',
                            margin: 0,
                            minHeight: '25px',
                        },
                        '& .MuiTimelineSeparator-root': {
                            marginTop: '0',
                        },
                    }}>
                    {timelineEvents.map((event, index) => (
                        <TimelineItem key={index}>
                            <TimelineSeparator>
                                <TimelineDot>{getTimelineIcon(event.status)}</TimelineDot>
                                {index < timelineEvents.length - 1 && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent sx={{ mt: '-15px' }}>
                                <Typography
                                    variant={'body1'}
                                    sx={{
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '24px',
                                        mt: '-20px',
                                    }}>
                                    {event.label}
                                </Typography>
                                <Typography
                                    variant={'body2'}
                                    color={'textSecondary'}
                                    sx={{
                                        flex: '0 0 200px',
                                        textAlign: 'right',
                                        fontFamily: 'monospace',
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '24px',
                                        justifyContent: 'flex-end',
                                        mt: '-20px',
                                    }}>
                                    {formatDate(event.date)}
                                </Typography>
                            </TimelineContent>
                        </TimelineItem>
                    ))}
                </Timeline>
            </Box>
        </Box>
    );
};
